import { STUDIO_BASE_URL, VITE_FORGE_URL } from '@constants';
import { trackEconomyAction } from 'analytics';
import ColorButton from 'components/Widget/Button/ColorButton';
import Chip from 'components/Widget/Chip';
import { Tier } from 'dragonmouse-shared/interfaces';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectIsGuest } from 'redux/db/reducers';
import * as S from './styles';

// Pricing constants
const PRO_MONTHLY = '12';
const PRO_YEARLY = '10.20';
const MAX_MONTHLY = '42';
const MAX_YEARLY = '35.70';
const UPGRADE_MAX_DIFFERENCE_MONTHLY = '30';
const UPGRADE_MAX_DIFFERENCE_YEARLY = '25.50';

interface TierCardProps {
	tier: Tier.TIER_1 | Tier.TIER_2; // tier to upgrade to
	variant: 'current' | 'upgrade' | 'upgradeToMax'; // upgradeToMax is for people already on tier 1 -- it shows the price difference
	renewalDate?: string;
	isYearly?: boolean;
	organizationId?: string; // if set, uses org-specific stripe endpoints
}

const TierCard = ({ variant, tier, renewalDate, isYearly, organizationId }: TierCardProps) => {
	const isGuest = useSelector((r: RootReducer) => selectIsGuest(r.db));
	const tierName = tier === Tier.TIER_1 ? 'Pro' : 'Max';

	const manageUrl = organizationId
		? `${VITE_FORGE_URL}/stripe/org-manage/${organizationId}`
		: `${VITE_FORGE_URL}/stripe/manage`;

	const onManageBillingClick = () => {
		trackEconomyAction('manage_subscription_clicked');
		window.open(manageUrl, '_blank');
	};

	const onUpgradeClick = (url: string) => {
		trackEconomyAction('upgrade_redirected');
		window.open(url);
	};

	const renderFeatureList = () => {
		const PRO_FEATURES = [
			'Full feature access',
			'200,000 monthly credits',
			'Visual edits, frame-to-code, Figma export',
			'Design system templates',
			'Chrome extension',
		];
		const MAX_FEATURES = [
			'Everything in Pro',
			'1,000,000 monthly credits',
			'More room for heavy generations & iterations',
			'Best for design system evolution',
			'No feature limits (same features, higher usage)',
		];
		const features = tier === Tier.TIER_1 ? PRO_FEATURES : MAX_FEATURES;
		return (
			<S.Features>
				{features.map((feature) => (
					<S.Feature key={feature} tier={tier}>
						<S.Checkmark tier={tier} viewBox="0 0 20 20" fill="currentColor">
							<path
								fillRule="evenodd"
								d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
								clipRule="evenodd"
							/>
						</S.Checkmark>
						{feature}
					</S.Feature>
				))}
			</S.Features>
		);
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (variant === 'current') {
		return (
			<S.Base isCurrent tier={tier}>
				<S.SubscriptionHeader>
					<S.PlanName tier={tier}>Current Plan: {tierName}</S.PlanName>
				</S.SubscriptionHeader>
				{renderFeatureList()}
				{renewalDate && <S.RenewalInfoBox>Next billing on {renewalDate}</S.RenewalInfoBox>}
				<ColorButton label="Manage Billing" color="blue" fullWidth onClick={onManageBillingClick} />
				<S.CancelLink href={manageUrl} target="_blank" rel="noopener noreferrer">
					Cancel subscription
				</S.CancelLink>
			</S.Base>
		);
	}

	if (variant === 'upgradeToMax') {
		const priceDifference = isYearly
			? UPGRADE_MAX_DIFFERENCE_YEARLY
			: UPGRADE_MAX_DIFFERENCE_MONTHLY;

		return (
			<S.Base tier={Tier.TIER_2}>
				<S.SubscriptionHeader>
					<S.PlanName tier={Tier.TIER_2}>Upgrade to Max</S.PlanName>
					<S.PlanPrice tier={Tier.TIER_2}>
						+${priceDifference}
						<S.PricePeriod>/mo</S.PricePeriod>
					</S.PlanPrice>
				</S.SubscriptionHeader>
				<S.UpgradeDescription>Move faster with more credits</S.UpgradeDescription>
				{renderFeatureList()}
				<ColorButton
					label="Upgrade to Max"
					color="blue"
					fullWidth
					onClick={() => onUpgradeClick(manageUrl)}
				/>
			</S.Base>
		);
	}

	// variant === 'upgrade'
	const price = isYearly
		? tier === Tier.TIER_1
			? PRO_YEARLY
			: MAX_YEARLY
		: tier === Tier.TIER_1
		? PRO_MONTHLY
		: MAX_MONTHLY;
	const upgradeUrl = isGuest
		? STUDIO_BASE_URL
		: organizationId
		? `${VITE_FORGE_URL}/stripe/org-session?tier=${tier}&interval=${
				isYearly ? 'year' : 'month'
		  }&organizationId=${organizationId}`
		: `${VITE_FORGE_URL}/stripe/session?tier=${tier}&interval=${isYearly ? 'year' : 'month'}`;

	return (
		<S.Base tier={tier}>
			<S.SubscriptionHeader>
				<S.PlanNameBlock>
					<S.PlanName tier={tier}>{tierName}</S.PlanName>
					<S.ChipSlot style={{ visibility: isYearly ? 'visible' : 'hidden' }}>
						<Chip label="Save 15%" color="blue" />
					</S.ChipSlot>
				</S.PlanNameBlock>
				<S.PlanPrice tier={tier}>
					${price}
					<S.PricePeriod>/mo</S.PricePeriod>
				</S.PlanPrice>
			</S.SubscriptionHeader>
			{renderFeatureList()}
			<ColorButton
				label={`Start with ${tierName}`}
				color="blue"
				fullWidth
				onClick={() => onUpgradeClick(upgradeUrl)}
			/>
		</S.Base>
	);
};

export default TierCard;
